import React, { useContext, useEffect, useState } from "react";

const AUTH_USER_KEY = "authUser";
const isBrowser = () => typeof window !== "undefined";

const readStorageValue = (storage) => {
  const raw = storage.getItem(AUTH_USER_KEY);
  if (raw == null) return { found: false, value: null };

  try {
    return { found: true, value: JSON.parse(raw) };
  } catch {
    return { found: true, value: null };
  }
}

const getStoredAuth = () => {
  if (!isBrowser()) { return null;}
  const local = readStorageValue(window.localStorage);
  if (local.found) return local.value;

  const session = readStorageValue(window.sessionStorage);
  return session.value;
};

export const storeAuth = (data, useSessionStorage) => {
  if (!isBrowser()) { return;}
  clearStoredAuth();
  let jsonString = null;
  try {
    jsonString = JSON.stringify(data);
  } catch {}
  if (jsonString != null) {
    if (useSessionStorage){
      window.sessionStorage.setItem(AUTH_USER_KEY, jsonString);
    } else {
      window.localStorage.setItem(AUTH_USER_KEY, jsonString);
    }
}
};

export const getAccessToken = () => {
  const user = getStoredAuth();
  return user?.token || "";
};

export const clearStoredAuth = () => {
  if (!isBrowser()) return;
  window.localStorage.removeItem(AUTH_USER_KEY);
  window.sessionStorage.removeItem(AUTH_USER_KEY);
};

export const AuthContext = React.createContext();

export const AuthContextProvider = ({ children }) => {
  const [userState, setUserState] = useState(() => getStoredAuth());

  const logoutUser = React.useCallback(() => {
    clearStoredAuth();       // clear session/token storage
    setUserState(null);  // reset user state
  }, []);

  const syncAuth = React.useCallback((e) => {
    if (e.key !== AUTH_USER_KEY) return;
    setUserState(getStoredAuth());
  }, []);
  // syncing auth state on different tabs
  useEffect(() => {
    window.addEventListener("storage", syncAuth);
    return () => window.removeEventListener("storage", syncAuth);
  }, [syncAuth]);

  const contextValue = React.useMemo(() => ({
    userState,
    setUserState,
    logoutUser,
  }), [userState, logoutUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthContextProvider");
  }
  return context;
};
